import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Layer } from '@feltmaps/js-sdk'
import { useFeltEmbed } from 'src/enviroment/lib/feltUtils'
import ProjectDetailsHeader from 'src/components/molecules/ProjectDetailsHeader'
import LayerLoadingLegend from 'src/components/molecules/LayerLoadingLegend'
import cometLoader from 'src/assets/comet-loader.svg'
import { api } from 'src/api/root'
import { LayerUploadStatus } from 'src/types/layer'
import { Project, ProjectStatusLayer } from 'src/types/projects'

type Props = {
  project: Project
  projectId: string
  onBack: () => void
  allLayersSettled?: boolean
  aoiLayerReady?: boolean
  layers?: ProjectStatusLayer[]
  legendLayers?: ProjectStatusLayer[]
  onMapReady?: () => void
  copaStatus?: LayerUploadStatus
  copaSummary?: string | null
  stage?: { id: string; name: string } | null
}

const FeltMapView: React.FC<Props> = ({
  project,
  projectId,
  onBack,
  allLayersSettled = false,
  aoiLayerReady = false,
  layers = [],
  legendLayers,
  onMapReady,
  copaStatus,
  copaSummary,
  stage,
}) => {
  const { mutateAsync: getExportLinkAsync } = api.project.exportLayer.useMutation()
  const [allLayers, setAllLayers] = useState<Layer[]>([])
  const [mapReady, setMapReady] = useState(false)
  const [overlayVisible, setOverlayVisible] = useState(true)

  // Memoize embedOptions — new reference every render would re-trigger useFeltEmbed effect endlessly.
  const embedOptions = useMemo(
    () => ({
      uiControls: {
        cooperativeGestures: false,
        fullScreenButton: false,
        zoomControls: false,
        showLegend: false,
      },
      ...(project?.latitude && project?.longitude && project?.initZoom
        ? {
            initialViewport: {
              center: { latitude: project.latitude, longitude: project.longitude },
              zoom: project.initZoom,
            },
          }
        : {}),
    }),
    [project?.latitude, project?.longitude, project?.initZoom],
  )

  const { felt, mapRef } = useFeltEmbed(project?.mapId, embedOptions)

  const copaProcessing = copaStatus === LayerUploadStatus.PROCESSING
  const fullyReady = mapReady && allLayersSettled && !copaProcessing

  // Crossfade: dismiss overlay and reveal Felt's native legend in the same tick
  // so there is no gap between the loading legend disappearing and the real one appearing.
  useEffect(() => {
    if (!felt || !fullyReady) return
    felt.updateUiControls({ showLegend: true })
    setOverlayVisible(false)
  }, [felt, fullyReady])

  const initializeSelection = useCallback(async () => {
    if (!felt) return
    try {
      const feltLayers = await felt.getLayers()
      const allLayers = feltLayers.filter((layer): layer is Layer => layer !== null)

      // Determine which layers to hide based on isVisible flag from projectLayerUploads
      const hiddenFeltLayerIds = new Set(
        (project?.projectLayerUploads ?? [])
          .filter((plu) => !plu.isVisible && plu.feltLayerId)
          .map((plu) => plu.feltLayerId),
      )

      const layersToHide = allLayers.filter((layer) => hiddenFeltLayerIds.has(layer.id))

      const standaloneLayerIdsToHide = layersToHide.filter((layer) => layer.groupId === null).map((layer) => layer.id)

      const groupIdsToHide = [
        ...new Set(layersToHide.map((layer) => layer.groupId).filter((groupId): groupId is string => groupId !== null)),
      ]

      const layerWithCategoricalLegend = layersToHide.filter((layer) => {
        const style: any = 'style' in layer ? layer.style : null
        return style?.type === 'categorical'
      })

      const layerLegendItems = await Promise.all(
        layerWithCategoricalLegend.map(async (layer) => {
          const legendItems = await felt.getLegendItems({ layerIds: [layer.id] })
          return {
            layerId: layer.id,
            itemIds: legendItems.map((item) => item?.id).filter(Boolean) as string[],
          }
        }),
      )

      const itemsToShow = layerLegendItems.flatMap(({ layerId, itemIds }) =>
        itemIds.map((itemId) => ({ layerId, id: itemId })),
      )

      if (standaloneLayerIdsToHide.length > 0) {
        await felt.setLayerVisibility({ hide: standaloneLayerIdsToHide })
      }
      if (groupIdsToHide.length > 0) {
        await felt.setLayerGroupVisibility({ hide: groupIdsToHide })
      }
      // ORDER MATTERS: first hide layers/groups, then show legend items nested in them
      if (itemsToShow.length > 0) {
        await felt.setLegendItemVisibility({ show: itemsToShow })
      }

      setAllLayers(allLayers)
      setMapReady(true)
      onMapReady?.()
    } catch (error) {
      console.error('Error in initializeSelection:', error)
    }
  }, [felt, project])

  useEffect(() => {
    if (!felt) return

    initializeSelection()

    const unsubscribe = felt.onSelectionChange({
      handler: async () => {},
    })

    return () => {
      unsubscribe()
    }
  }, [felt, initializeSelection])

  const onExportLayer = async (layer: Layer) => {
    try {
      await getExportLinkAsync({ projectId, layerId: layer.id })
    } catch (error) {
      console.error('Error exporting layer:', error)
    }
  }

  return (
    <>
      <ProjectDetailsHeader
        project={project}
        onBack={onBack}
        layers={allLayers}
        onExport={onExportLayer}
        copaStatus={copaStatus}
        copaSummary={copaSummary}
        stage={stage}
      />
      <div className="relative flex-1">
        <div className="h-full" ref={mapRef} />

        <div
          className={`absolute inset-0 z-10 rounded-t-lg bg-gray-950/80 transition-opacity duration-500 ${
            overlayVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <div className="flex h-full items-center justify-center">
            <img src={cometLoader} alt="" width={56} height={56} className="animate-spin" />
          </div>
        </div>

        {overlayVisible && (layers.length > 0 || copaStatus === LayerUploadStatus.PROCESSING || !allLayersSettled) && (
          <div className="absolute bottom-3 left-3 top-3 z-20 flex flex-col">
            <LayerLoadingLegend layers={legendLayers ?? layers} aoiLayerReady={aoiLayerReady} copaStatus={copaStatus} />
          </div>
        )}
      </div>
    </>
  )
}

export default FeltMapView
